/**
 * Tax type identifiers matching the backend TaxTypes enum.
 */
export enum TaxTypes {
  SalesTax = 0,
  UseTax = 1,
  BOTax = 2,
  NoTax = 3,
  HST = 4,
  PST = 5,
  GeneralTax = 6
}

/**
 * Result of a single tax rate item calculation.
 */
export interface TaxCalculationResult {
  taxCost: number;
  taxSellPrice: number;
  taxCostVisibility: boolean;
  taxTDCVisibility: boolean;
}

/**
 * Cost/sell-price sources needed by the tax calculation engine.
 * Collected from the form at calculation time to keep the engine stateless.
 */
export interface TaxCostSources {
  // Material costs (raw cost values)
  siemensMaterialCost: number;
  outsideMaterialCost: number;
  mechanicalMaterialCost: number;
  electricalMaterialCost: number;
  totalWarrantyCost: number;
  totalShippingCost: number;

  // Labor costs (raw cost values)
  projectManagementLaborCost: number;
  engineeringLaborCost: number;
  networkEngineeringLaborCost: number;
  qualificationEngineeringLaborCost: number;
  cyberSecurityEngineeringLaborCost: number;
  energyEngineeringLaborCost: number;
  electricalEngineeringLaborCost: number;
  graphicsLaborCost: number;
  programmingLaborCost: number;
  specialistLaborCost: number;
  digitalDeploymentLaborCost: number;
  mechanicalLaborCost: number;
  electricianLaborCost: number;

  // Misc/risk/travel costs
  miscCost: number;
  travelExpenseCost: number;
  riskCost: number;
  autoRiskCost: number;
  productivityReserveCost: number;

  // Subcontractor cost
  subcontractorCostTotal: number;

  // Material cost total (for PST/HST calculation)
  materialCostTotal: number;
}

/**
 * Configuration inputs for the sell price calculation engine.
 * Decoupled from the DTO shape so the calculation logic remains pure and testable.
 * Used across all estimate sections (material, labor, subcontractor, misc/risk/travel, etc.).
 */
export interface SellPriceConfig {
  /** true = MarkUp mode, false = Gross Margin mode */
  isMarkUpSelected: boolean;

  /**
   * The global GM% (or MU%) from OtherMetaData.grossMargin.soldASGMPercent.
   * Used as the uniform percentage in Fixed Margin mode.
   * In Line Item mode this serves as the fallback when no per-row value exists.
   */
  soldAsGMPercent: number;

  /**
   * Pricing mode flag from EstimateTab:
   *   false  → Fixed Margin  – every row uses soldAsGMPercent
   *   null   → Line Item     – each row uses its own GM% from row.grossMarginPercentage
   *   true   → Fixed Price   – (not yet implemented)
   */
  isFixedPriceSelected: boolean | null;

  /**
   * Technology fee percentage used to derive the tech fee cost from the
   * sum of other sections' sell prices.  Sourced from FeesData.
   * Formula:  divisor = (techFeePercent / 100) / (1 - technologyFeeGMPercent / 100)
   *           TechFeeCost = (SumOfSellPrices / (1 - divisor)) × (techFeePercent / 100)
   */
  techFeePercent: number;

  /**
   * GM% specifically for the technology fee row.
   * Hardcoded to 30 % for now; will be made user-configurable later.
   */
  technologyFeeGMPercent: number;

  /**
   * Tax type for the current estimate sourced from EstimateSettings.taxTypeID.
   * Determines which tax calculation path and which tax UI rows are visible.
   */
  taxTypeID: number;

  /**
   * isReadOnlyMode is sourced from CommonDataService and indicates whether the current estimate is in read-only mode.
   * When true, all sell price calculations should still run, but no updates should be patched to the UI model.
   * This allows us to display calculated sell prices in read-only mode without modifying the underlying data.
   */
  isEstimateReadOnly: boolean;
}

/**
 * Result of a single row sell price calculation.
 * Both grossMarginPercentage and markUpPercentage are always computed
 * so the caller can decide which one to display based on the active mode.
 */
export interface SellPriceCalculationResult {
  sellPrice: number;
  gmDollars: number;
  /** GM% = ((sellPrice - cost) / sellPrice) × 100 */
  grossMarginPercentage: number;
  /** MU% = ((sellPrice - cost) / cost) × 100 */
  markUpPercentage: number;
}
